
import { Component, ContentChildren, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID, QueryList, forwardRef } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import * as moment_tz from 'moment-timezone';
import { Subscription } from 'rxjs';
import { ErrorMessages } from '../../common/constants';
import { Alert, Thing } from '../../model';
import { AuthenticationService } from '../../service/authentication.service';
import { DateRangeName, DateRangeService, PeriodVariable } from '../../service/date-range.service';
import { FieldService } from '../../service/field.service';
import { AbstractThingContextService } from '../../shared/class/abstract-thing-context-service.class';
import { MetricDetailComponent } from '../../shared/component';
import { ErrorUtility } from '../../utility/error-utility';
import { EventListWidgeService } from '../alert-list-v2/event-list/event-list-widget.service';
import { AmChart5Component } from '../amchart5/am-chart5.component';
import { ThingListWidgetV2Service } from '../thing-list/thing-list-widget-v2.service';
import { DailySummaryGridWidgetService } from './daily-summary-grid-widget.service';

@Component({
    selector: 'daily-summary-grid-widget',
    template: require('./daily-summary-grid-widget.component.html'),
    providers: [ThingListWidgetV2Service, DailySummaryGridWidgetService, EventListWidgeService]
})
export class DailySummaryGridWidgetComponent extends AmChart5Component implements OnInit, OnDestroy {

    @Input() title: string;

    @Input() height: string = '300px';

    @Input() description: string;

    @Input() config: DailySummaryGridWidgetConfig;

    @Input() inputs: ReferenceInput;

    @ContentChildren(MetricDetailComponent) metricComponents: QueryList<MetricDetailComponent>;

    locale: string;
    timezone: string;
    loadedData: boolean;
    error: string;
    isDataEmpty: boolean;
    selectedChartData: ChartData;

    private thing: Thing;
    private range: DateRange<moment.Moment>;
    private periodFieldRefTimeoutId: any;
    private periodFieldSubscription: Subscription;
    private series: any;
    private xAxis: any;
    private yAxis: any;
    private chart: any;
    private eventAdvancedSearchBody: any;
    private selectedChartCell: any;
    private eventNamesInput: string[];

    constructor(
        @Inject(PLATFORM_ID) platformId: Object,
        @Inject(forwardRef(() => NgZone)) zone: NgZone,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => DateRangeService)) private dateRangeService: DateRangeService,
        @Inject(forwardRef(() => FieldService)) protected fieldService: FieldService,
        @Inject(forwardRef(() => DailySummaryGridWidgetService)) protected widgetService: DailySummaryGridWidgetService
    ) {
        super(platformId, zone);
    }

    ngOnInit(): void {
        this.thing = this.thingContextService.getCurrentThing();
        const user = this.authenticationService.getUser();
        this.timezone = user.timezone || 'UTC';
        this.locale = user.locale || user.language || 'en';
        if (this.config?.defaultPeriod) {
            const daterange = this.dateRangeService.getCustomDateRangeByName(this.config?.defaultPeriod);
            if (daterange) {
                this.range = new DateRange(daterange.range.start, daterange.range.end);
            }
        }
        this.eventNamesInput = this.config?.eventNames ? this.config?.eventNames.split(',') : null;
        if (this.eventNamesInput?.length > 0) {
            this.config.eventQuery = [{ property: 'name', predicate: 'eq', value: this.config?.eventNames }];
            if (this.inputs) {
                this.inputs.eventQueryRef = null;
            }
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.unsubscribeFromPeriodField();
    }

    private subscribeToPeriodField(): void {
        if (this.inputs?.periodRef) {
            this.periodFieldRefTimeoutId = setTimeout(() => {
                this.loadChartData()
            }, 5000);
            this.periodFieldSubscription = this.fieldService.subscribeToFields([this.inputs?.periodRef]).subscribe(fieldsMap => {
                const period: PeriodVariable = fieldsMap ? fieldsMap[this.inputs?.periodRef] : null;
                if (period != null) {
                    this.clearPeriodFieldRefTimeout();
                    this.range = new DateRange(moment_tz.tz(period.start, this.timezone), moment_tz.tz(period.end, this.timezone));
                    this.loadChartData();
                }
            });
        }
    }

    private clearPeriodFieldRefTimeout(): void {
        if (this.periodFieldRefTimeoutId) {
            clearTimeout(this.periodFieldRefTimeoutId);
            this.periodFieldRefTimeoutId = null;
        }
    }

    private unsubscribeFromPeriodField(): void {
        if (this.inputs?.periodRef) {
            this.fieldService.unsubscribeFromFields([this.inputs?.periodRef]);
            this.periodFieldSubscription.unsubscribe();
        }
        this.clearPeriodFieldRefTimeout();
    }

    getChartId(): string {
        return 'daily-summary-grid-widget';
    }

    initChart(): void {
        if (this.thing) {
            // Create chart
            // https://www.amcharts.com/docs/v5/charts/xy-chart/
            this.chart = this.root.container.children.push(this.am5xy.XYChart.new(this.root, {
                panX: true,
                panY: false,
                paddingLeft: 0,
                layout: this.root.verticalLayout
            }));

            // Sets timezone and locale
            if (this.timezone == "UTC") {
                this.root.utc = true;
            } else {
                this.root.timezone = this.am5.Timezone.new(this.timezone);
            }
            let locale = window['localeUtils']?.getAmChartLocale ? window['localeUtils'].getAmChartLocale() : null;
            if (locale && locale != "en_US") {
                locale = window["am5locales_" + locale];
                if (locale) {
                    this.root.locale = locale;
                }
            }

            // Create axes and their renderers
            let yRenderer = this.am5xy.AxisRendererY.new(this.root, {
                visible: false,
                inversed: true,
                minorGridEnabled: true
            });

            yRenderer.grid.template.set("visible", false);

            this.yAxis = this.chart.yAxes.push(this.am5xy.CategoryAxis.new(this.root, {
                maxDeviation: 0,
                renderer: yRenderer,
                categoryField: "category"
            }));

            let xRenderer = this.am5xy.AxisRendererX.new(this.root, {
                visible: false,
                minorGridEnabled: true,
                minGridDistance: 10
            });

            xRenderer.grid.template.set("visible", false);

            this.xAxis = this.chart.xAxes.push(this.am5xy.CategoryDateAxis.new(this.root, {
                renderer: xRenderer,
                categoryField: "dateTimestamp",
                baseInterval: { timeUnit: "day", count: 1 },
                gridIntervals: [
                    { timeUnit: "day", count: 1 },
                    { timeUnit: "month", count: 1 }
                ],
                maxDeviation: 0
            }));
            this.xAxis.get("dateFormats")["day"] = "d";
            this.xAxis.get("periodChangeDateFormats")["day"] = "[bold]MMM";

            // Create series
            // https://www.amcharts.com/docs/v5/charts/xy-chart/#Adding_series
            this.series = this.chart.series.push(this.am5xy.ColumnSeries.new(this.root, {
                stroke: this.am5.color(0xffffff),
                clustered: false,
                xAxis: this.xAxis,
                yAxis: this.yAxis,
                categoryXField: "dateTimestamp",
                categoryYField: "category",
                valueField: "value"
            }));

            this.series.columns.template.setAll({
                tooltipText: this.getTooltipText(),
                strokeOpacity: 1,
                strokeWidth: 2,
                width: this.am5.percent(100),
                height: this.am5.percent(100)
            });

            // Customize tooltip settings
            let tooltip = this.am5.Tooltip.new(this.root, {
                getFillFromSprite: false,
                getStrokeFromSprite: false
            });
            tooltip.get("background").setAll({
                fill: this.am5.color(0xffffff),
                fillOpacity: 1,
                stroke: this.am5.color(0x0e1a1d)
            });
            this.series.set("tooltip", tooltip);

            this.series.columns.template.adapters.add("tooltipText", function (text, target) {
                return target.dataItem?.dataContext?.value?.tooltipText ? text : null;
            });

            // Set up heat rules
            // https://www.amcharts.com/docs/v5/concepts/settings/heat-rules/
            this.series.set("heatRules", [{
                target: this.series.columns.template,
                dataField: "value",
                customFunction: function (sprite, min, max, value) {
                    sprite.set("fill", value.color);
                }
            }]);

            // Add scrollbars
            this.chart.set("scrollbarX", this.am5.Scrollbar.new(this.root, {
                orientation: "horizontal",
                visible: false
            }));
            this.chart.zoomOutButton.set("forceHidden", true);
            let scrollbarX = this.chart.get("scrollbarX");
            scrollbarX.startGrip.setAll({
                disabled: true,
                visible: false
            });
            scrollbarX.endGrip.setAll({
                disabled: true,
                visible: false
            });

            // Set zoom on resize and data validation
            this.xAxis.events.on("boundschanged", (ev) => {
                this.zoomChart(ev);
            });
            this.xAxis.events.on("datavalidated", (ev) => {
                this.zoomChart(ev);
            });
            this.yAxis.events.on("datavalidated", (ev) => {
                this.setMinimumHeight(ev);
            });

            // Add event count
            if (this.config?.showEventCount) {
                this.series.bullets.push((root, series, dataItem) => {
                    if (dataItem.dataContext.value.highlightCount > 0 && dataItem.dataContext.value.eventHighlights?.length > 0) {
                        return this.am5.Bullet.new(root, {
                            sprite: this.am5.Label.new(root, {
                                fill: this.am5.color(0xffffff),
                                populateText: true,
                                centerX: this.am5.p50,
                                centerY: this.am5.p50,
                                fontSize: 16,
                                text: "{value.highlightCount}"
                            })
                        });
                    }
                });
            }

            // Cell click event
            this.series.columns.template.adapters.add("cursorOverStyle", function (value, target) {
                return target.dataItem?.dataContext?.value?.highlightCount > 0 ? 'pointer' : null;
            });
            this.series.columns.template.events.on("click", (ev) => {
                if (ev.target.dataItem?.dataContext?.value?.highlightCount > 0) {
                    this.resetSelectedCellOpacity();
                    ev.target.set("opacity", 0.5);
                    this.zone.run(() => {
                        this.selectedChartCell = ev.target;
                        this.selectedChartData = ev.target.dataItem?.dataContext;
                    });
                }
            });

            // Set data
            // https://www.amcharts.com/docs/v5/charts/xy-chart/#Setting_data

            if (this.inputs?.periodRef) {
                this.subscribeToPeriodField();
            } else if (!this.inputs?.eventQueryRef && !this.config?.eventQuery) {
                this.loadChartData();
            }
        }
    }

    private resetSelectedCellOpacity() {
        if (this.selectedChartCell) {
            this.selectedChartCell.set("opacity", 1);
            this.selectedChartCell = null;
        }
    }

    private zoomChart(ev: any): void {
        if (ev.target.dataItems.length) {
            let scrollbarX = this.chart.get("scrollbarX");
            let xWidth = this.xAxis.width();
            const minCellWidth = 48;
            const maxZoomedElements = Math.floor(xWidth / minCellWidth);
            if (ev.target.dataItems.length > maxZoomedElements) {
                scrollbarX.set("visible", true);
                ev.target.zoomToIndexes(ev.target.dataItems.length - maxZoomedElements, ev.target.dataItems.length);
            } else {
                scrollbarX.set("visible", false);
                ev.target.zoomToIndexes(0, ev.target.dataItems.length);
            }
        }
    }

    private setMinimumHeight(ev: any): void {
        if (ev.target.data?.length) {
            const minCellHeight = 48;
            const minHeight = ev.target.data?.length * minCellHeight;
            if (this.yAxis.height() < minHeight) {
                const extraHeight = this.chart.height() - this.yAxis.height();
                this.chart.root.dom.style.height = minHeight + extraHeight + "px";
            }
        }
    }

    private getTooltipText(): string {
        let text = "{value.tooltipText}";
        return text;
    }

    private loadChartData(): void {
        this.loadedData = false;
        this.closeDetails();
        this.loadData().then(data => {
            this.zone.run(() => this.loadedData = true);
            this.isDataEmpty = !data.chartData.length;
            if (this.isDataEmpty) {
                this.series.data.setAll([]);
                this.yAxis.data.setAll([]);
                this.xAxis.data.setAll([]);
            } else {
                this.series.data.setAll(data.chartData);

                // Auto-populate X and Y axis category data
                this.yAxis.data.setAll(data.yAxisValues.map((item) => { return { category: item } }));
                this.xAxis.data.setAll(data.xAxisValues.map((item) => { return { dateTimestamp: item.startTimestamp } }));

                // Make stuff animate on load
                // https://www.amcharts.com/docs/v5/concepts/animations/#Initial_animation
                this.chart.appear(1000, 100);
            }
        }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR));
    }

    private loadData(): Promise<{ chartData: ChartData[], xAxisValues: xAxisValues[], yAxisValues: string[] }> {
        if (!this.range) {
            const daterange = this.dateRangeService.getCustomDateRangeByName(DateRangeName.LAST_30_DAYS);
            this.range = new DateRange(daterange.range.start, daterange.range.end);
        }
        const startTimestamp = this.range.start.valueOf();
        const endTimestamp = this.range.end.valueOf();
        this.eventAdvancedSearchBody = this.eventAdvancedSearchBody || {};
        this.eventAdvancedSearchBody.minEndTimestamp = startTimestamp;
        this.eventAdvancedSearchBody.endTimestamp = endTimestamp;   // maxStartTimestamp
        const xAxisValues = this.widgetService.computeXAxisValues(this.range.start.clone(), this.range.end.clone());
        return Promise.all([this.widgetService.loadEventData(this.eventAdvancedSearchBody, xAxisValues, this.config), this.widgetService.loadMetricData(xAxisValues, this.metricComponents, startTimestamp, endTimestamp, this.config)]).then(results => {
            const eventChartData = results[0].data;
            const eventChartCategories = this.eventNamesInput?.length > 0 ? this.eventNamesInput : results[0].categories;
            const metricChartData = results[1].data;
            const metricChartCategories = results[1].categories;
            return { chartData: eventChartData.concat(metricChartData), xAxisValues: xAxisValues, yAxisValues: eventChartCategories.concat(metricChartCategories) };
        });
    }

    protected executeAdvancedSearch(advancedSearchBody: any): void {
        this.eventAdvancedSearchBody = advancedSearchBody;
        if (!this.inputs?.periodRef && advancedSearchBody.startTimestamp && advancedSearchBody.endTimestamp) {
            this.range = new DateRange(moment_tz.tz(advancedSearchBody.startTimestamp, this.timezone), moment_tz.tz(advancedSearchBody.endTimestamp, this.timezone));
        }
        delete this.eventAdvancedSearchBody.startTimestamp;
        delete this.eventAdvancedSearchBody.endTimestamp;
        this.loadChartData();
    }

    closeDetails(): void {
        this.selectedChartData = null;
        this.resetSelectedCellOpacity();
    }

}

export class DailySummaryGridWidgetConfig {
    defaultPeriod: DateRangeName;
    showEventCount: boolean;
    showEventSeverity: boolean;
    sortByOccurrences: boolean;
    eventQuery: { property: string, predicate: string, value: any }[];
    eventNames: string;
}

class ReferenceInput {
    periodRef: string;
    eventQueryRef: string;
}

export class ChartData {
    category: string;
    dateTimestamp: string;
    value: ChartDataValue;
}

export class ChartDataValue {
    color: string;
    highlightCount: number;
    tooltipText: string;
    eventHighlights: Alert[];
    metricDataHighlights: MetricDataHighlight[];
}

export class MetricDataHighlight {
    timestamp: number;
    metricName: string;
    metricLabel: string;
    value: any;
    unit: string;
    filter: any;
    filterArg: any;
}

export class xAxisValues {
    startTimestamp: number;
    endTimestamp: number;
}

export class DailySummaryGridWidgetMetricConfig {
    group: string;
    highlight: DailySummaryGridWidgetHighlightType;
}

export enum DailySummaryGridWidgetHighlightType {
    VALUE_CHANGE = 'VALUE_CHANGE',
    VALUE_SEVERITY = 'VALUE_SEVERITY'
}